import { useState } from 'react';
import {
  DollarSign,
  Calendar,
  MessageSquare,
  Loader2,
  TrendingDown,
  AlertCircle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { rfqApi, CreateNegotiationDto } from '@/api/services';
import { getErrorMessage } from '@/api/client';

interface StartNegotiationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rfqId: string;
  lineId: string;
  responseId: string;
  carrierName: string;
  currentRate: number;
  currency?: string;
  onSuccess?: () => void;
}

export function StartNegotiationDialog({
  open,
  onOpenChange,
  rfqId,
  lineId,
  responseId,
  carrierName,
  currentRate,
  currency = 'USD',
  onSuccess,
}: StartNegotiationDialogProps) {
  const [loading, setLoading] = useState(false);
  const [targetRate, setTargetRate] = useState('');
  const [deadline, setDeadline] = useState('');
  const [message, setMessage] = useState('');

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const targetRateNum = parseFloat(targetRate) || 0;
  const savingsAmount = currentRate - targetRateNum;
  const savingsPercent = currentRate > 0 ? (savingsAmount / currentRate) * 100 : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!targetRate || targetRateNum <= 0) {
      toast.error('Please enter a valid target rate');
      return;
    }

    if (!deadline) {
      toast.error('Please set a response deadline');
      return;
    }

    if (targetRateNum >= currentRate) {
      toast.error('Target rate should be lower than the current rate');
      return;
    }

    setLoading(true);
    try {
      const dto: CreateNegotiationDto = {
        lineId,
        responseId,
        targetRate: targetRateNum,
        deadline: new Date(deadline).toISOString(),
        message: message.trim() || undefined,
      };

      await rfqApi.startNegotiation(rfqId, dto);
      toast.success('Negotiation started successfully');
      onOpenChange(false);
      resetForm();
      onSuccess?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTargetRate('');
    setDeadline('');
    setMessage('');
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  // Calculate minimum deadline (tomorrow)
  const minDeadline = new Date();
  minDeadline.setDate(minDeadline.getDate() + 1);
  const minDeadlineStr = minDeadline.toISOString().split('T')[0];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Start Rate Negotiation
            </DialogTitle>
            <DialogDescription>Negotiate a better rate with {carrierName}</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Current Rate Display */}
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Current Quote Rate</div>
              <div className="text-2xl font-bold">{formatCurrency(currentRate)}</div>
              <div className="text-sm text-muted-foreground">{carrierName}</div>
            </div>

            {/* Target Rate Input */}
            <div className="space-y-2">
              <Label htmlFor="targetRate" className="flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Target Rate
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <Input
                  id="targetRate"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Enter your target rate"
                  value={targetRate}
                  onChange={(e) => setTargetRate(e.target.value)}
                  className="pl-7"
                  required
                />
              </div>
              {targetRateNum > 0 && targetRateNum < currentRate && (
                <div className="flex items-center gap-2 text-sm text-status-success-foreground">
                  <TrendingDown className="w-4 h-4" />
                  <span>
                    Potential savings: {formatCurrency(savingsAmount)} ({savingsPercent.toFixed(1)}
                    %)
                  </span>
                </div>
              )}
              {targetRateNum >= currentRate && targetRateNum > 0 && (
                <div className="flex items-center gap-2 text-sm text-status-warning-foreground">
                  <AlertCircle className="w-4 h-4" />
                  <span>Target should be lower than current rate</span>
                </div>
              )}
            </div>

            {/* Deadline Input */}
            <div className="space-y-2">
              <Label htmlFor="deadline" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Response Deadline
              </Label>
              <Input
                id="deadline"
                type="date"
                min={minDeadlineStr}
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">The carrier must respond by this date</p>
            </div>

            {/* Message Input */}
            <div className="space-y-2">
              <Label htmlFor="message" className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Message (Optional)
              </Label>
              <Textarea
                id="message"
                placeholder="Add a message to explain your target rate or provide context..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || targetRateNum >= currentRate}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                'Start Negotiation'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
